import { useCallback, useEffect, useState } from "react";
import { Status, PendingHintCode, FailedHintCode, } from "./BankIdTypes";
const POLL_TIMER = 3000;
const SHORT_POLL_TIMER = 1000;
const MAX_QR_CODES = 28;
const QR_CODE_TIMER = 1000;
const dummyPromise = {
    then: () => ({ catch: () => { } }),
    catch: () => { },
};
export function useBankId({ onComplete, onFailed, onPollError, initPollFn, qrCodePollFn, pollFn, pollInterval = POLL_TIMER, }) {
    var _a, _b;
    const [state, setState] = useState({
        status: "IDLE",
        latestResponse: undefined,
    });
    const [openOnOtherDevice, setOpenOnOtherDevice] = useState(false);
    const reset = useCallback(() => {
        setState({
            status: "IDLE",
            latestResponse: undefined,
        });
    }, []);
    const initiate = useCallback((...args) => {
        if (state.status !== "IDLE" && state.status !== "FAILED") {
            return dummyPromise;
        }
        setState((oldState) => ({
            status: "PENDING-INIT",
            latestResponse: oldState.status === "FAILED" ? oldState.latestResponse : undefined,
        }));
        return initPollFn(...args)
            .then((response) => {
            if (response.status === Status.FAILED) {
                setState({
                    status: "FAILED",
                    latestResponse: response,
                });
                onFailed === null || onFailed === void 0 ? void 0 : onFailed(response);
                return;
            }
            setState({
                status: "INIT-COMPLETE",
                latestResponse: response,
            });
            return response;
        })
            .catch((error) => {
            setState({
                status: "FAILED",
                latestResponse: {
                    status: Status.FAILED,
                    hintCode: FailedHintCode.UNKNOWN,
                },
            });
            throw error;
        });
    }, [initPollFn, onFailed, state.status]);
    useEffect(() => {
        let isStale = false;
        let timeoutId = null;
        if (!pollFn) {
            const pollFnWasRemoved = state.status === "PENDING-POLL";
            if (pollFnWasRemoved) {
                setState({
                    status: "IDLE",
                    latestResponse: undefined,
                });
            }
            return;
        }
        const recoverPollingFromFreshPageLoad = state.status === "IDLE";
        if (recoverPollingFromFreshPageLoad) {
            setState({
                status: "PENDING-POLL",
                latestResponse: undefined,
                qrCode: undefined,
            });
            return;
        }
        else if (state.status === "INIT-COMPLETE") {
            setState({
                status: "PENDING-POLL",
                latestResponse: state.latestResponse,
                qrCode: undefined,
            });
            return;
        }
        else if (state.status !== "PENDING-POLL") {
            return;
        }
        timeoutId = setTimeout(() => {
            pollFn()
                .then((response) => {
                timeoutId = null;
                if (response.status === Status.COMPLETE) {
                    onComplete(response, reset);
                    return;
                }
                if (isStale) {
                    return;
                }
                if (response.status === Status.FAILED) {
                    setState({
                        status: "FAILED",
                        latestResponse: response,
                    });
                    onFailed === null || onFailed === void 0 ? void 0 : onFailed(response);
                    return;
                }
                setState((oldState) => ({
                    status: "PENDING-POLL",
                    latestResponse: response,
                    qrCode: oldState.status === "PENDING-POLL"
                        ? oldState.qrCode
                        : undefined,
                }));
            })
                .catch((error) => {
                if (isStale) {
                    return;
                }
                timeoutId = null;
                setState({
                    status: "FAILED",
                    latestResponse: {
                        status: Status.FAILED,
                        hintCode: FailedHintCode.UNKNOWN,
                    },
                });
                onPollError === null || onPollError === void 0 ? void 0 : onPollError(error);
            });
        }, typeof state.latestResponse === "undefined"
            ? SHORT_POLL_TIMER
            : pollInterval);
        return () => {
            isStale = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };
    }, [
        onComplete,
        onPollError,
        pollFn,
        reset,
        state.status,
        state.latestResponse,
        onFailed,
        pollInterval,
    ]);
    const hasLatestResponse = typeof state.latestResponse !== "undefined";
    const openedOrderInBankIDApp = ((_a = state.latestResponse) === null || _a === void 0 ? void 0 : _a.hintCode) === PendingHintCode.STARTED ||
        ((_b = state.latestResponse) === null || _b === void 0 ? void 0 : _b.hintCode) === PendingHintCode.USERSIGN;
    useEffect(() => {
        let isStale = false;
        let timeoutId = null;
        let retries = 0;
        if (openOnOtherDevice &&
            state.status === "PENDING-POLL" &&
            !openedOrderInBankIDApp &&
            hasLatestResponse) {
            function pollQr() {
                qrCodePollFn()
                    .then((response) => {
                    var _a;
                    const qrCode = (_a = response === null || response === void 0 ? void 0 : response.code) !== null && _a !== void 0 ? _a : undefined;
                    if (isStale) {
                        return;
                    }
                    setState((oldState) => {
                        if (oldState.status === "PENDING-POLL") {
                            return {
                                status: "PENDING-POLL",
                                latestResponse: oldState.latestResponse,
                                qrCode,
                            };
                        }
                        return oldState;
                    });
                })
                    .catch(() => {
                })
                    .finally(() => {
                    if (isStale) {
                        return;
                    }
                    if (retries >= MAX_QR_CODES) {
                        setState({
                            status: "FAILED",
                            latestResponse: {
                                status: Status.FAILED,
                                hintCode: FailedHintCode.EXPIREDTRANSACTION,
                            },
                        });
                        return;
                    }
                    retries += 1;
                    timeoutId = setTimeout(pollQr, QR_CODE_TIMER);
                });
            }
            pollQr();
        }
        return () => {
            isStale = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };
    }, [
        openOnOtherDevice,
        qrCodePollFn,
        openedOrderInBankIDApp,
        state.status,
        hasLatestResponse,
    ]);
    switch (state.status) {
        case "IDLE":
            return {
                pollStatus: "IDLE",
                initiate,
                latestResponse: undefined,
                qrCode: undefined,
                reset,
                setOpenOnOtherDevice,
            };
        case "PENDING-INIT": {
            if (typeof state.latestResponse !== "undefined") {
                return {
                    pollStatus: "FAILED",
                    initiate,
                    latestResponse: state.latestResponse,
                    qrCode: undefined,
                    reset,
                    setOpenOnOtherDevice,
                };
            }
            else {
                return {
                    pollStatus: "IDLE",
                    initiate,
                    latestResponse: undefined,
                    qrCode: undefined,
                    reset,
                    setOpenOnOtherDevice,
                };
            }
        }
        case "INIT-COMPLETE":
        case "PENDING-POLL":
            return {
                pollStatus: "PENDING",
                initiate: undefined,
                latestResponse: state.latestResponse,
                qrCode: state.status === "PENDING-POLL" ? state.qrCode : undefined,
                reset,
                setOpenOnOtherDevice,
            };
        case "FAILED":
            return {
                pollStatus: "FAILED",
                initiate,
                latestResponse: state.latestResponse,
                reset,
                qrCode: undefined,
                setOpenOnOtherDevice,
            };
    }
}
